import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "../../components/ui";
import {
  aiContentGetHistory,
  type AiContentPost,
} from "../../utils/aiContent";
import type { AiContentOutletContext } from "./AiContentLayout";
import { aiOutlineBtn, aiActionGhostBtn, aiActionLinkBtn } from "./aiContentUi";

const HistoryPage = () => {
  const { ownerId, userId, workspaceId, ownerName } =
    useOutletContext<AiContentOutletContext>();
  const [posts, setPosts] = useState<AiContentPost[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewPost, setViewPost] = useState<AiContentPost | null>(null);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const data = await aiContentGetHistory(
        { ownerId, userId, workspaceId },
        p,
        20
      );
      setPosts(Array.isArray(data?.posts) ? data.posts : []);
      setTotalPages(Number(data?.totalPages) || 0);
      setPage(Number(data?.page) || p);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ownerId) void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  return (
    <div>
      <h2 className="mb-3 text-[15px] font-semibold tracking-tight text-flow-config-text">
        Post History
      </h2>
      {loading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : !posts.length ? (
        <div className="rounded-[10px] border border-line bg-white px-6 py-10 text-center text-sm text-muted">
          No history yet for this account.
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-line bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-paper text-xs uppercase text-muted">
              <tr>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Account</th>
                <th className="px-4 py-3 font-semibold">Topic</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Post</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {posts.map((post) => (
                <tr key={post._id}>
                  <td className="whitespace-nowrap px-4 py-3 text-muted">
                    {post.publishedAt || post.scheduledAt
                      ? new Date(
                          post.publishedAt || post.scheduledAt || ""
                        ).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-flow-config-text">{ownerName}</td>
                  <td className="px-4 py-3 text-muted">
                    {post.topic || `Day ${post.dayIndex}`}
                  </td>
                  <td className="px-4 py-3 capitalize text-brand-blue">
                    {String(post.status || "").replace(/_/g, " ")}
                  </td>
                  <td className="max-w-xs px-4 py-3">
                    <span className="line-clamp-2 text-flow-config-text">
                      {post.postText}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={aiActionLinkBtn}
                        onClick={() => setViewPost(post)}
                      >
                        View
                      </Button>
                      {post.linkedinPostUrl ? (
                        <a
                          href={post.linkedinPostUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-8 items-center px-2.5 text-xs font-semibold text-brand-blue"
                        >
                          Open LinkedIn
                        </a>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="sm"
                        className={aiActionGhostBtn}
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(post.postText);
                            toast.success("Copied post text");
                          } catch {
                            toast.error("Copy failed");
                          }
                        }}
                      >
                        Duplicate
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 ? (
            <div className="flex items-center justify-between border-t border-line px-4 py-3">
              <Button
                variant="outline"
                size="sm"
                className={aiOutlineBtn}
                isDisabled={page <= 1}
                onClick={() => void load(page - 1)}
              >
                Previous
              </Button>
              <span className="text-xs text-muted">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className={aiOutlineBtn}
                isDisabled={page >= totalPages}
                onClick={() => void load(page + 1)}
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {viewPost ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 p-4"
          onClick={() => setViewPost(null)}
        >
          <div
            className="max-h-[80vh] w-full max-w-lg overflow-auto rounded-[10px] bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm font-semibold text-flow-config-text">
              Day {viewPost.dayIndex} · {statusLabel(viewPost.status)}
            </p>
            <p className="mt-3 whitespace-pre-wrap text-sm text-flow-config-text">
              {viewPost.postText}
            </p>
            {viewPost.lastError &&
            viewPost.status !== "published" &&
            viewPost.status !== "skipped" ? (
              <p className="mt-3 text-xs text-danger">{viewPost.lastError}</p>
            ) : null}
            <Button
              className={`mt-4 ${aiOutlineBtn}`}
              variant="outline"
              size="sm"
              onClick={() => setViewPost(null)}
            >
              Close
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

function statusLabel(status: string) {
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default HistoryPage;
